import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	EventEmitter,
	input,
	Input,
	OnChanges,
	Output,
	signal,
	SimpleChanges,
	viewChild,
	inject,
} from '@angular/core';
import { DEFAULT_EMPTY_STATE, Option } from '../utils/option';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { SingleSelectDropdownDialogComponent } from '@cumlaude/shared-components-dialogs';
import { firstValueFrom } from 'rxjs';

import { IconComponent } from '@cumlaude/shared-components-icons';

@Component({
	selector: 'app-single-select-dropdown',
	templateUrl: './single-select-dropdown.component.html',
	styleUrls: ['./single-select-dropdown.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [IconComponent],
})
export class SingleSelectDropdownComponent<T> implements OnChanges {
	private readonly dialog = inject(Dialog);
	private readonly overlay = inject(Overlay);

	@Input()
	showSearchInput: boolean = false;

	@Input()
	showEmpty: string = DEFAULT_EMPTY_STATE;

	@Input()
	searchInput: string = '';

	@Input()
	placeholder!: string;

	enabled = input(true);

	opties = input<Option<T>[]>([]);

	label = input<string>();

	@Input()
	selectedOptie: Option<T> | undefined;

	@Output()
	onOptieClicked = new EventEmitter<T>();

	@Output()
	onSearchInput = new EventEmitter<string>();

	@Output()
	onShowDropdown = new EventEmitter<boolean>();

	showDropdown = signal(false);
	isHovered = signal(false);

	protected readonly iconName = computed(() => (this.showDropdown() ? 'chevron-up' : 'chevron-down'));

	protected readonly iconColor = computed(() => {
		if (!this.enabled()) return 'background-2';
		return this.showDropdown() || this.isHovered() ? 'primary-1' : 'typography-5';
	});

	dropdownHostElement = viewChild.required<ElementRef>('dropdownHost');

	private dialogRef: DialogRef<Option<T>, SingleSelectDropdownDialogComponent<T>> | undefined;

	async openDialog() {
		if (!this.enabled()) return;

		this.onShowDropdown.emit(true);
		this.showDropdown.set(true);

		const dropdownElement = this.dropdownHostElement();
		const boundingClientRect = dropdownElement.nativeElement.getBoundingClientRect();

		this.dialogRef = this.dialog.open(SingleSelectDropdownDialogComponent<T>, {
			data: {
				opties: this.opties,
				selectedOptie: this.selectedOptie,
				showEmpty: this.showEmpty,
				showSearchInput: this.showSearchInput,
				label: this.label(),
			},
			backdropClass: 'cdk-overlay-transparent-backdrop',
			positionStrategy: this.overlay
				.position()
				.flexibleConnectedTo(dropdownElement)
				.withFlexibleDimensions(false)
				.withPositions([
					{
						originX: 'center',
						originY: 'bottom',
						overlayX: 'center',
						overlayY: 'top',
						offsetY: 1,
					},
					{
						originX: 'center',
						originY: 'top',
						overlayX: 'center',
						overlayY: 'bottom',
						offsetY: -1,
					},
				]),
			width: boundingClientRect.width,
		});

		const instance = this.dialogRef.componentInstance!;
		const subscription = instance.onSearchInput?.subscribe((value) => this.onSearchInput.emit(value));
		instance.searchInput.set(this.searchInput);

		const newOption = await firstValueFrom(this.dialogRef.closed);
		if (newOption) {
			this.selectedOptie = newOption;
			this.onOptieClicked.emit(newOption.value);
		}

		subscription?.unsubscribe();
		this.onShowDropdown.emit(false);
		this.showDropdown.set(false);
	}

	ngOnChanges(changes: SimpleChanges): void {
		const componentInstance = this.dialogRef?.componentInstance;
		if ('searchInput' in changes && !changes['searchInput'].isFirstChange() && componentInstance)
			componentInstance.searchInput.set(changes['searchInput'].currentValue);
	}
}
